import { Route } from '@angular/router';
import { LOCALIZED_SHARED_ROUTES, SHARED_ROUTE_IDS } from '@lexplus-shared-lib';
import { IsLoggedInGuard } from '@lexplus-authentication';
import { AdminOnlyGuard } from '../../guards/admin-only.guard';

/**
 * The Kunden page and its four tabs, one Kunde's detail page and its six, and one of that Kunde's
 * Standorte and its five. Every tab is a route of its own so a tab is linkable and survives a reload; the tabs of one page all load the
 * same page component, which reads the active tab from the route's `routeId`.
 */
export function KundenPageRoute(localeId: string): Route {
  const locale = localeId?.split('-')[0] || 'en';

  const tabRouteIds = [
    SHARED_ROUTE_IDS.ADMIN_CUSTOMERS_EXISTING,
    SHARED_ROUTE_IDS.ADMIN_CUSTOMERS_ACQUISITION,
    SHARED_ROUTE_IDS.ADMIN_CUSTOMERS_NEWSLETTER,
    SHARED_ROUTE_IDS.ADMIN_CUSTOMERS_ALL,
  ];

  const detailTabRouteIds = [
    SHARED_ROUTE_IDS.ADMIN_CUSTOMER_DETAIL_OVERVIEW,
    SHARED_ROUTE_IDS.ADMIN_CUSTOMER_DETAIL_SITES,
    SHARED_ROUTE_IDS.ADMIN_CUSTOMER_DETAIL_CONTACTS,
    SHARED_ROUTE_IDS.ADMIN_CUSTOMER_DETAIL_ORDERS,
    SHARED_ROUTE_IDS.ADMIN_CUSTOMER_DETAIL_DOCUMENTS,
    SHARED_ROUTE_IDS.ADMIN_CUSTOMER_DETAIL_NOTES,
  ];

  const standortDetailTabRouteIds = [
    SHARED_ROUTE_IDS.ADMIN_SITE_DETAIL_OVERVIEW,
    SHARED_ROUTE_IDS.ADMIN_SITE_DETAIL_PUBLISHERS,
    SHARED_ROUTE_IDS.ADMIN_SITE_DETAIL_TOPICS,
    SHARED_ROUTE_IDS.ADMIN_SITE_DETAIL_UPDATES,
    SHARED_ROUTE_IDS.ADMIN_SITE_DETAIL_DOCUMENTS,
  ];

  return {
    path: LOCALIZED_SHARED_ROUTES[SHARED_ROUTE_IDS.ADMIN_CUSTOMERS][locale],
    canActivate: [IsLoggedInGuard, AdminOnlyGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: LOCALIZED_SHARED_ROUTES[SHARED_ROUTE_IDS.ADMIN_CUSTOMERS_EXISTING][locale], // Default tab
      },
      ...tabRouteIds.map((routeId) => ({
        path: LOCALIZED_SHARED_ROUTES[routeId][locale],
        loadComponent: () => import('./pages/kunden-page/kunden-page.component').then((m) => m.KundenPageComponent),
        data: { routeId },
      })),
      // After the list tabs: the router takes the first match, so the reverse order would read
      // a tab's path word as a Kunde id.
      {
        path: ':kundeId',
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: LOCALIZED_SHARED_ROUTES[SHARED_ROUTE_IDS.ADMIN_CUSTOMER_DETAIL_OVERVIEW][locale], // Default tab
          },
          ...detailTabRouteIds.map((routeId) => ({
            path: LOCALIZED_SHARED_ROUTES[routeId][locale],
            loadComponent: () => import('./pages/kunde-detail-page/kunde-detail-page.component').then((m) => m.KundeDetailPageComponent),
            data: { routeId },
          })),
          // After the tabs, for the same reason as `:kundeId`: the Standorte tab owns the bare
          // `standorte` path, and one of its Standorte is that path plus an id.
          {
            path: `${LOCALIZED_SHARED_ROUTES[SHARED_ROUTE_IDS.ADMIN_CUSTOMER_DETAIL_SITES][locale]}/:standortId`,
            children: [
              {
                path: '',
                pathMatch: 'full',
                redirectTo: LOCALIZED_SHARED_ROUTES[SHARED_ROUTE_IDS.ADMIN_SITE_DETAIL_OVERVIEW][locale], // Default tab
              },
              ...standortDetailTabRouteIds.map((routeId) => ({
                path: LOCALIZED_SHARED_ROUTES[routeId][locale],
                loadComponent: () =>
                  import('./pages/standort-detail-page/standort-detail-page.component').then((m) => m.StandortDetailPageComponent),
                data: { routeId },
              })),
            ],
            // The host's id, like the Kunde level above it.
            data: { routeId: SHARED_ROUTE_IDS.ADMIN_CUSTOMERS },
          },
        ],
        // The host's id, so a tab-less `/kunden/42` still resolves to the Kunden area when the
        // language is switched. The tabs name themselves.
        data: { routeId: SHARED_ROUTE_IDS.ADMIN_CUSTOMERS },
      },
    ],
    // Carried on the parent as well, so switching the language from a tab-less `/kunden`
    // still resolves to this page rather than to the dashboard.
    data: { routeId: SHARED_ROUTE_IDS.ADMIN_CUSTOMERS },
  };
}
